/**
 * src/db/index.ts — IndexedDB setup via `idb` (§9).
 *
 * Two independent version numbers, never conflated: the IndexedDB STRUCTURAL version
 * here (bumped only when stores/indexes change) and each record's data-shape
 * `schemaVersion` (handled by the migration chain in migrations.ts).
 */
import { type DBSchema, type IDBPDatabase, openDB } from 'idb';

export const DB_NAME = 'sbloc-dashboard';
/** Structural version — bump ONLY when object stores or indexes change. */
export const DB_VERSION = 1;

export interface QuarantineEntry {
  id: string;
  raw: unknown;
  error: string;
  quarantinedAt: string;
}

export interface BackupEntry {
  key: string;
  createdAt: string;
  scenarios: unknown[];
}

export interface AppStateEntry {
  key: string;
  value: unknown;
}

export interface MetaEntry {
  key: string;
  value: unknown;
}

export interface PalDb extends DBSchema {
  scenarios: { key: string; value: Record<string, unknown>; indexes: { 'by-updatedAt': string } };
  appState: { key: string; value: AppStateEntry };
  quarantine: { key: string; value: QuarantineEntry };
  backups: { key: string; value: BackupEntry };
  meta: { key: string; value: MetaEntry };
}

let dbPromise: Promise<IDBPDatabase<PalDb>> | null = null;

/** Open (and cache) the database, creating stores on first use. */
export function getDb(): Promise<IDBPDatabase<PalDb>> {
  if (!dbPromise) {
    dbPromise = openDB<PalDb>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        const scenarios = db.createObjectStore('scenarios', { keyPath: 'id' });
        scenarios.createIndex('by-updatedAt', 'updatedAt');
        db.createObjectStore('appState', { keyPath: 'key' });
        db.createObjectStore('quarantine', { keyPath: 'id' });
        db.createObjectStore('backups', { keyPath: 'key' });
        db.createObjectStore('meta', { keyPath: 'key' });
      },
    });
  }
  return dbPromise;
}

/** Close the open connection (if any) and drop the cache (used by tests). */
export async function closeDb(): Promise<void> {
  if (dbPromise) {
    const db = await dbPromise;
    db.close();
    dbPromise = null;
  }
}

/** Reset the cached connection without closing (rarely needed). */
export function resetDbConnection(): void {
  dbPromise = null;
}
